import { create } from 'zustand'
import type { Order } from '@/lib/vision/schemas'

export const MAX_BULK_POS = 100

interface BulkSelectionState {
  orders: Order[]
  toggle: (order: Order) => void
  selectMany: (orders: readonly Order[]) => void
  remove: (orderId: string) => void
  removeMany: (orderIds: readonly string[]) => void
  move: (orderId: string, direction: -1 | 1) => void
  /** Drop `orderId` at `toIndex` (its new priority), shifting the others. */
  reorder: (orderId: string, toIndex: number) => void
  clear: () => void
  keepTenant: (tenantId: string) => void
}

/**
 * Deliberately memory-only. Product explicitly chose no draft recovery for v1, so reloading the
 * page clears the working batch instead of restoring quantities that may have stale inventory.
 */
export const useBulkSelectionStore = create<BulkSelectionState>(set => ({
  orders: [],
  toggle: order =>
    set(state => {
      const existing = state.orders.find(row => row.id === order.id)
      if (existing) {
        return { orders: state.orders.filter(row => row.id !== order.id) }
      }
      const sameTenant = state.orders.filter(row => row.tenantId === order.tenantId)
      const selectedChannel = sameTenant[0]?.channel
      if (
        selectedChannel &&
        normalizeChannel(selectedChannel) !== normalizeChannel(order.channel)
      ) {
        return { orders: sameTenant }
      }
      if (sameTenant.length >= MAX_BULK_POS) return { orders: sameTenant }
      return { orders: [...sameTenant, order] }
    }),
  selectMany: incoming =>
    set(state => {
      const tenantId = incoming.find(order => order.tenantId)?.tenantId
      if (!tenantId) return state
      let orders = state.orders.filter(order => order.tenantId === tenantId)
      for (const order of incoming) {
        if (order.tenantId !== tenantId || orders.some(row => row.id === order.id)) continue
        const selectedChannel = orders[0]?.channel
        if (
          selectedChannel &&
          normalizeChannel(selectedChannel) !== normalizeChannel(order.channel)
        ) {
          continue
        }
        if (orders.length >= MAX_BULK_POS) break
        orders = [...orders, order]
      }
      return { orders }
    }),
  remove: orderId => set(state => ({ orders: state.orders.filter(order => order.id !== orderId) })),
  removeMany: orderIds =>
    set(state => {
      const removed = new Set(orderIds)
      return { orders: state.orders.filter(order => !removed.has(order.id)) }
    }),
  move: (orderId, direction) =>
    set(state => {
      const from = state.orders.findIndex(order => order.id === orderId)
      const to = from + direction
      if (from < 0 || to < 0 || to >= state.orders.length) return state
      const orders = [...state.orders]
      const [moved] = orders.splice(from, 1)
      if (!moved) return state
      orders.splice(to, 0, moved)
      return { orders }
    }),
  reorder: (orderId, toIndex) =>
    set(state => {
      const from = state.orders.findIndex(order => order.id === orderId)
      const to = Math.max(0, Math.min(toIndex, state.orders.length - 1))
      if (from < 0 || from === to) return state
      const orders = [...state.orders]
      const [moved] = orders.splice(from, 1)
      if (!moved) return state
      orders.splice(to, 0, moved)
      return { orders }
    }),
  clear: () => set({ orders: [] }),
  keepTenant: tenantId =>
    set(state => {
      const tenantOrders = state.orders.filter(order => order.tenantId === tenantId)
      const selectedChannel = tenantOrders[0]?.channel
      return {
        orders: selectedChannel
          ? tenantOrders.filter(
              order => normalizeChannel(order.channel) === normalizeChannel(selectedChannel),
            )
          : [],
      }
    }),
}))

function normalizeChannel(channel: string): string {
  return channel.trim().toLowerCase()
}
